import { useContext, useEffect } from 'react';
import { UNSUBCRIBE_TO_GAME_UPDATES } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppSelector } from 'hooks';
import { useExitToNextTable } from 'hooks/useExitToNextTable';
import { findAnnouncedTournamentTablesToExit } from 'utils/announcedTournamentTables';
import { markRecentlyExitedTable } from 'utils/recentlyExitedTables';

/**
 * Exits every open table whose tournament is back to Announced while the
 * player has no seat there (see findAnnouncedTournamentTablesToExit) — out of
 * the nav, the grid and the subscription. Headless; mounted once app-level.
 *
 * The view moves only when that table is the one on screen in single-table
 * view: to the next open table, or the lobby if it was the last.
 */
const AnnouncedTournamentTableExit = () => {
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const playerTournamentDetails = useAppSelector(
    (state) => (state as any).lobby?.playerTournamentDetails
  );
  const tournamentList = useAppSelector(
    (state) => (state as any).lobby?.tournament?.tournamentList
  );
  const sitAndGoList = useAppSelector((state) => (state as any).lobby?.tournament?.sitAndGoList);
  const { socket } = useContext(SocketContext);
  const { activeView, activeTableInfo } = useContext(ViewManagerContext);
  const exitToNextTable = useExitToNextTable();

  useEffect(() => {
    const lobbyTournaments = [...(tournamentList || []), ...(sitAndGoList || [])];
    const exiting = findAnnouncedTournamentTablesToExit(
      tables,
      playerTournamentDetails,
      lobbyTournaments
    );
    // The whole pass's set, so the table on screen hands the view to one that
    // survives it rather than to another table this pass removes.
    const alsoLeaving = exiting.map((t) => t.gameID);
    exiting.forEach(({ gameID, gameType }) => {
      // Before the unsubscribe, so an in-flight broadcast can't re-add it.
      markRecentlyExitedTable(gameID);
      socket?.emit(UNSUBCRIBE_TO_GAME_UPDATES, { gameID, tableID: gameID, game: gameType });
      exitToNextTable(gameID, {
        navigate: activeView === 'table' && String(activeTableInfo?.gameID) === gameID,
        alsoLeaving
      });
    });
  }, [
    tables,
    playerTournamentDetails,
    tournamentList,
    sitAndGoList,
    socket,
    activeView,
    activeTableInfo,
    exitToNextTable
  ]);

  return null;
};

export default AnnouncedTournamentTableExit;
